import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { BOOKING_URL, branch, hoursSummary } from '../data/siteData';
import salonImg from '../assets/salon.jpeg';

const slides = [
  {
    id: 1,
    tag: 'Batıkent\'te Premium Güzellik',
    title: 'Güzelliğiniz için\nen iyi deneyim',
    subtitle: 'Protez tırnak, nail art, kirpik lifting ve kaş laminasyonu hizmetlerimizle size özel bakım.',
    cta: 'Randevu Al',
    ctaHref: BOOKING_URL,
    image: salonImg,
    gradient: 'linear-gradient(135deg, rgba(255,20,147,0.65) 0%, rgba(100,0,60,0.60) 100%)',
  },
  {
    id: 2,
    tag: 'Velux AVM — Batıkent',
    title: 'Uzman ellerde\npremium bakım',
    subtitle: 'Sertifikalı güzellik uzmanlarımız her işlemi titizlikle uygular. Kalıcı oje, nail art ve daha fazlası.',
    cta: 'Hizmetleri Gör',
    ctaHref: '#services',
    image: 'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=1800&q=85',
    gradient: 'linear-gradient(135deg, rgba(100,0,60,0.80) 0%, rgba(199,21,133,0.60) 100%)',
  },
  {
    id: 3,
    tag: hoursSummary,
    title: 'Kendinize zaman\nayırın',
    subtitle: 'Batıkent\'te güzellik deneyiminizi şimdi rezerve edin. Ücretsiz danışma için bizi arayın.',
    cta: 'Bize Ulaşın',
    ctaHref: '#contact',
    image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=1800&q=85',
    gradient: 'linear-gradient(135deg, rgba(30,0,80,0.80) 0%, rgba(255,20,147,0.55) 100%)',
  },
];

const INTERVAL = 6000;

const Hero: React.FC = () => {
  const [current, setCurrent]     = useState(0);
  const [transitioning, setTrans] = useState(false);
  const [progress, setProgress]   = useState(0);

  const goTo = useCallback((i: number) => {
    if (transitioning) return;
    setTrans(true);
    setProgress(0);
    setTimeout(() => { setCurrent(i); setTrans(false); }, 400);
  }, [transitioning]);

  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo]);

  useEffect(() => {
    const t = setInterval(next, INTERVAL);
    return () => clearInterval(t);
  }, [next]);

  useEffect(() => {
    setProgress(0);
    const start = Date.now();
    const raf = () => {
      const pct = Math.min(((Date.now() - start) / INTERVAL) * 100, 100);
      setProgress(pct);
      if (pct < 100) requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
  }, [current]);

  const slide = slides[current];

  return (
    <section id="home" className="hero-modern">
      {/* Progress bar */}
      <div className="swiper-progress-bar">
        <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Slides */}
      {slides.map((s, i) => (
        <div
          key={s.id}
          className="hero-slide-modern"
          style={{
            position: 'absolute', inset: 0,
            opacity: i === current ? 1 : 0,
            transition: 'opacity 0.7s ease',
          }}
        >
          <img src={s.image} alt="" />
          <div className="hero-overlay" style={{ background: s.gradient }} />
        </div>
      ))}

      {/* Floating shapes */}
      <div className="hero-shapes">
        <div className="shape shape-1" />
        <div className="shape shape-2" />
        <div className="shape shape-3" />
        <div className="shape shape-4" />
      </div>

      {/* Content */}
      <div className="hero-content-modern">
        <div className="container">
          <div className="hero-content-inner">
            {/* Badge */}
            <div
              className="slide-badge"
              style={{
                opacity: transitioning ? 0 : 1,
                transform: transitioning ? 'translateY(10px)' : 'translateY(0)',
                transition: 'all 0.5s ease',
              }}
            >
              <span className="badge-line" />
              {slide.tag}
            </div>

            {/* Title */}
            <h1
              className="hero-title-modern"
              style={{
                opacity: transitioning ? 0 : 1,
                transform: transitioning ? 'translateY(20px)' : 'translateY(0)',
                transition: 'all 0.5s ease 75ms',
              }}
            >
              {slide.title}
            </h1>

            {/* Subtitle */}
            <p
              className="hero-subtitle-modern"
              style={{
                opacity: transitioning ? 0 : 1,
                transform: transitioning ? 'translateY(20px)' : 'translateY(0)',
                transition: 'all 0.5s ease 120ms',
              }}
            >
              {slide.subtitle}
            </p>

            {/* CTA */}
            <div
              className="hero-cta"
              style={{
                opacity: transitioning ? 0 : 1,
                transform: transitioning ? 'translateY(20px)' : 'translateY(0)',
                transition: 'all 0.5s ease 160ms',
              }}
            >
              <a
                href={slide.ctaHref}
                {...(slide.ctaHref.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="btn btn-modern-primary"
              >
                {slide.cta}
              </a>
              <a href="#services" className="btn btn-ghost-light">
                Hizmetlerimiz
              </a>
            </div>

            {/* Trust badges */}
            <div
              className="hero-badge-group"
              style={{
                opacity: transitioning ? 0 : 1,
                transform: transitioning ? 'translateY(20px)' : 'translateY(0)',
                transition: 'all 0.5s ease 200ms',
              }}
            >
              <div className="trust-badge">
                <span className="star">★★★★★</span>
                1000+ Müşteri
              </div>
              <div className="trust-badge">
                ✨ Profesyonel Ekip
              </div>
              <div className="trust-badge">
                📍 {branch.shortName}, Ankara
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Prev / Next */}
      <button className="swiper-btn-modern" onClick={prev} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 20 }}>
        <ChevronLeft size={18} />
      </button>
      <button className="swiper-btn-modern" onClick={next} style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 20 }}>
        <ChevronRight size={18} />
      </button>

      {/* Dot pagination */}
      <div className="swiper-dots">
        {slides.map((_, i) => (
          <button key={i} className={`swiper-dot${i === current ? ' active' : ''}`} onClick={() => goTo(i)} />
        ))}
      </div>
    </section>
  );
};

export default Hero;
